import React, { useMemo, useRef } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { commonStyles } from "../commonStyles";

// Toolbar per the create-event transcription spec (fix-pack contract §5):
// Paragraph style dropdown, bold/italic/underline, alignment, link. The
// bullet-list variant is opt-in (`withBulletList`) so callers that need lists
// (e.g. sponsorship benefits) can add them without changing this default.
const DEFAULT_MODULES = {
  toolbar: [
    [{ header: [false, 1, 2, 3] }],
    ["bold", "italic", "underline"],
    [{ align: "" }, { align: "center" }, { align: "justify" }],
    ["link"],
  ],
};

// Group order per the edit-sponsor-level mock (PAR-EW-17):
// Paragraph | B/I/U | bullet list | alignment | link.
const BULLET_LIST_MODULES = {
  toolbar: [
    [{ header: [false, 1, 2, 3] }],
    ["bold", "italic", "underline"],
    [{ list: "bullet" }],
    [{ align: "" }, { align: "center" }, { align: "justify" }],
    ["link"],
  ],
};

const stripHtml = (html: string): string =>
  html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

let editorInstanceCounter = 0;

type RichTextEditorProps = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  maxLength?: number;
  minHeight?: number;
  withBulletList?: boolean;
};

const RichTextEditor = ({
  value,
  onChange,
  placeholder,
  maxLength,
  minHeight = 140,
  withBulletList = false,
}: RichTextEditorProps): React.ReactElement => {
  const quillRef = useRef<ReactQuill>(null);
  const plainLength = useMemo(() => stripHtml(value || "").length, [value]);
  // Unique per-instance class so editors with different minHeight props do
  // not overwrite each other's style rules.
  const instanceClass = useRef(`rich-text-editor-${(editorInstanceCounter += 1)}`);

  const handleChange = (html: string) => {
    if (maxLength) {
      const editor = quillRef.current?.getEditor();
      // Hard stop at maxLength: trim anything typed/pasted past the limit.
      // (Quill's getLength includes a trailing newline, hence the -1.)
      if (editor && editor.getLength() - 1 > maxLength) {
        editor.deleteText(maxLength, editor.getLength() - 1 - maxLength);
        return; // deleteText re-fires onChange with the trimmed content
      }
    }
    onChange(html);
  };

  return (
    <div className={`rich-text-editor ${instanceClass.current}`}>
      <style>{`
        .rich-text-editor { border: 1px solid #D1D5DB; border-radius: 10px; background: #fff; }
        .rich-text-editor:focus-within { border-color: ${commonStyles.cpaGold}; }
        .rich-text-editor .ql-toolbar.ql-snow { border: 0; border-bottom: 1px solid #E5E7EB; border-radius: 10px 10px 0 0; background: #F9FAFB; }
        .rich-text-editor .ql-container.ql-snow { border: 0; font-size: 0.95rem; }
        /* Editor body text is black, not inherited grey (July 9, 2026). */
        .rich-text-editor .ql-editor { color: #030712; }
        .rich-text-editor .ql-snow .ql-picker.ql-header .ql-picker-label:not([data-value])::before,
        .rich-text-editor .ql-snow .ql-picker.ql-header .ql-picker-item:not([data-value])::before { content: "Paragraph"; }
        .${instanceClass.current} .ql-container { min-height: ${minHeight}px; }
        .${instanceClass.current} .ql-editor { min-height: ${minHeight}px; }
      `}</style>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={handleChange}
        modules={withBulletList ? BULLET_LIST_MODULES : DEFAULT_MODULES}
        placeholder={placeholder}
      />
      {maxLength ? (
        <p className="rounded-b-[10px] border-t border-gray-200 bg-gray-50 px-4 py-2 text-left text-[13px] text-gray-600">
          {plainLength}/{maxLength} characters
        </p>
      ) : null}
    </div>
  );
};

export default RichTextEditor;
